import { useEffect, useState } from "react";
import { apiClient } from "../apiClient";
import { useTranslation } from "react-i18next";
import jsPDF from "jspdf";

export default function ClientsPage() {
  // ✅ ALL HOOKS FIRST — no conditions before this line
  const { t } = useTranslation();
  const [clients, setClients] = useState([]);
  const [newClient, setNewClient] = useState({
    name: "",
    contact: "",
    telephone: "",
    whatsapp: "",
    address: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // ✅ Role check AFTER hooks
  const userRole = localStorage.getItem("userRole");

  // ✅ Auth-protected data fetching
  useEffect(() => {
    // ✅ FIXED: Added braces and early return
    if (!["admin", "manager", "partner"].includes(userRole)) {
      setLoading(false);
      return;
    }

    const fetchClients = async () => {
      try {
        const data = await apiClient.get("/clients/");
        setClients(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchClients();
  }, [userRole]); // ✅ include userRole in deps

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNewClient((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // ✅ Allow partner to view, but only admin/manager can edit
    if (userRole !== "admin" && userRole !== "manager") return;

    setSaving(true);
    setError(null);

    try {
      const created = await apiClient.post("/clients/", newClient);
      setClients((prev) => [...prev, created]);
      setNewClient({
        name: "",
        contact: "",
        telephone: "",
        whatsapp: "",
        address: "",
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handlePdfExport = () => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text(t("clients"), 14, 20);

    if (clients.length === 0) {
      doc.setFontSize(12);
      doc.text(t("noClients"), 14, 30);
    } else {
      doc.setFontSize(12);
      let y = 30;
      clients.forEach((c, i) => {
        const line1 = `${i + 1}. ${c.name}`;
        const line2 = `   Tel: ${c.telephone || "-"} | WhatsApp: ${c.whatsapp || "-"}`;
        const line3 = c.address ? `   Address: ${c.address}` : "";

        doc.text(line1, 14, y);
        y += 6;
        doc.text(line2, 14, y);
        y += 6;
        if (line3) {
          doc.text(line3, 14, y);
          y += 6;
        }
        y += 4;
      });
    }

    doc.save("clients.pdf");
  };

  // ✅ Render access denied UI if not authorized
  if (!["admin", "manager", "partner"].includes(userRole)) {
    return (
      <div className="p-6 text-center">
        <h2 className="text-xl text-red-600">❌ Access Denied</h2>
        <p>Only admin, manager, or partner can view clients.</p>
      </div>
    );
  }

  // ✅ Main UI
  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto space-y-6 bg-[#F5E9D8]">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center">
        <h2 className="text-2xl font-bold text-[#23265E]">{t("clients")}</h2>
        <button
          onClick={handlePdfExport}
          className="mt-3 sm:mt-0 flex items-center bg-[#F76B4C] hover:bg-[#E65A3C] text-white px-4 py-2 rounded-lg transition shadow"
        >
          {t("exportPdf")}
        </button>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="bg-red-100 border border-red-300 text-red-700 p-3 rounded text-sm">
          ❌ {error}
        </div>
      )}

      {/* Add New Client Form */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-[#23265E] border-opacity-20">
        <h3 className="text-lg font-semibold text-[#23265E] mb-3">{t("addClient")}</h3>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block font-medium text-[#23265E] mb-1">{t("clientName")}</label>
            <input
              type="text"
              name="name"
              value={newClient.name}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-[#23265E] border-opacity-30 rounded focus:outline-none focus:ring-1 focus:ring-[#F76B4C]"
              required
            />
          </div>

          <div>
            <label className="block font-medium text-[#23265E] mb-1">{t("contactInfo")}</label>
            <input
              type="text"
              name="contact"
              value={newClient.contact}
              onChange={handleChange}
              placeholder="Optional contact person"
              className="w-full px-3 py-2 border border-[#23265E] border-opacity-30 rounded focus:outline-none focus:ring-1 focus:ring-[#F76B4C]"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input
              type="text"
              name="telephone"
              value={newClient.telephone}
              onChange={handleChange}
              placeholder="Telephone"
              className="w-full px-3 py-2 border border-[#23265E] border-opacity-30 rounded focus:outline-none focus:ring-1 focus:ring-[#F76B4C]"
            />
            <input
              type="text"
              name="whatsapp"
              value={newClient.whatsapp}
              onChange={handleChange}
              placeholder="WhatsApp"
              className="w-full px-3 py-2 border border-[#23265E] border-opacity-30 rounded focus:outline-none focus:ring-1 focus:ring-[#F76B4C]"
            />
          </div>

          <div>
            <input
              type="text"
              name="address"
              value={newClient.address}
              onChange={handleChange}
              placeholder="Address"
              className="w-full px-3 py-2 border border-[#23265E] border-opacity-30 rounded focus:outline-none focus:ring-1 focus:ring-[#F76B4C]"
            />
          </div>

          <button
            type="submit"
            disabled={saving}
            className={`w-full py-2 rounded-lg transition ${
              saving
                ? "bg-gray-400 text-gray-200 cursor-not-allowed"
                : "bg-[#F76B4C] hover:bg-[#E65A3C] text-white"
            }`}
          >
            {saving ? t("adding") + "..." : t("addClient")}
          </button>
        </form>
      </div>

      {/* Client List */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-[#23265E] border-opacity-20">
        <h3 className="font-semibold text-[#23265E] mb-3">
          {t("clientList")} ({clients.length})
        </h3>

        {loading ? (
          <p className="text-center text-[#23265E] text-sm italic">{t("loading")}...</p>
        ) : clients.length === 0 ? (
          <p className="text-center text-[#23265E] text-sm italic">{t("noClients")}</p>
        ) : (
          <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
            {clients.map((c) => (
              <div
                key={c.id}
                className="p-3 border border-[#23265E] border-opacity-20 rounded-lg bg-[#F5E9D8]"
              >
                <div className="font-semibold text-[#23265E]">{c.name}</div>
                <div className="text-sm text-[#23265E] mt-1 space-y-1">
                  {c.telephone && (
                    <div>📞 <span className="font-medium">Tel:</span> {c.telephone}</div>
                  )}
                  {c.whatsapp && (
                    <div>💬 <span className="font-medium">WhatsApp:</span> {c.whatsapp}</div>
                  )}
                  {c.address && (
                    <div>📍 <span className="font-medium">Address:</span> {c.address}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}